import React, { useContext, useState, useEffect, useMemo } from "react";
import { Box, Typography, LinearProgress, Select, MenuItem } from "@mui/material";
import { styled } from "@mui/system";
import { useParams, Link } from "react-router-dom";
import { firestore } from "../firebaseConfig";
import PetsIcon from "@mui/icons-material/Pets";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import UserContext from "../context/userContext";
import GlobalStateContext from "../context/globalStateContext";
import AuthModal from "../components/AuthModal";
import Header from "../components/Header";
import FooterComponent from "../components/Footer";
import { calculateDogTimeRemaining, formatDogAge, groupPledgesByAnimalId, sortDogsByUrgency } from "../utils/HELPER_FUNCTIONS";

// Using exact styling from DogProfilePage/LandingPage
const PageContainer = styled(Box)({
  width: "100%",
  minHeight: "100vh",
  backgroundColor: "#000000",
  color: "#FFFFFF",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  overflowX: "hidden",
  boxSizing: "border-box",
  "&::-webkit-scrollbar": {
    display: "none",
  },
});

const MainContainer = styled(Box)(({ theme }) => ({
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "120px 24px 60px 24px",
  width: "100%",
  boxSizing: "border-box",
  [theme.breakpoints.down("sm")]: {
    padding: "100px 16px 40px 16px",
  },
}));

const ShelterHeader = styled(Box)(({ theme }) => ({
  marginBottom: "48px",
  textAlign: "center",
  width: "100%",
  boxSizing: "border-box",
  [theme.breakpoints.down("sm")]: {
    marginBottom: "32px",
  },
}));

const ShelterName = styled(Typography)(({ theme }) => ({
  fontSize: "36px",
  fontWeight: "700",
  color: "#FFFFFF",
  marginBottom: "8px",
  lineHeight: "1.1",
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
  },
}));

const ShelterLocation = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  color: "#6B7280",
  fontWeight: "400",
  marginBottom: "8px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

const ShelterContactInfo = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  marginBottom: "20px",
  alignItems: "center",
});

const ShelterContactItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "14px",
  color: "#9CA3AF",
  fontWeight: "400",
  "& svg": {
    fontSize: "18px",
    color: "#6B7280",
    flexShrink: 0,
  },
  "& span": {
    wordBreak: "break-word",
  },
  "& a": {
    color: "#10B981",
    textDecoration: "none",
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#059669",
      textDecoration: "underline",
    },
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
    "& svg": {
      fontSize: "16px",
    },
  },
}));

const ResultsCount = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  fontSize: "13px",
  color: "#6B7280",
  fontWeight: "400",
  marginBottom: "24px",
});

const ErrorContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "400px",
  color: "#9CA3AF",
  fontSize: "18px",
  gap: "16px",
});

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "16px",
  marginBottom: "32px",
  flexWrap: "wrap",
  justifyContent: "center",
  width: "100%",
  boxSizing: "border-box",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
  },
}));

const ControlGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flex: 1,
  minWidth: "200px",
  maxWidth: "300px",
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    minWidth: "100%",
  },
}));

const ControlLabel = styled(Typography)({
  fontSize: "14px",
  fontWeight: "600",
  color: "#9CA3AF",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const StyledSelect = styled(Select)({
  backgroundColor: "#1F2937",
  color: "#FFFFFF",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#374151",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#4B5563",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#10B981",
  },
  "& .MuiSelect-icon": {
    color: "#9CA3AF",
  },
  "& .MuiSelect-select": {
    padding: "12px 14px",
  },
});

const DogGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "24px",
  marginBottom: "60px",
  width: "100%",
  boxSizing: "border-box",
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
    marginBottom: "40px",
  },
}));

const DogCard = styled(Box)({
  backgroundColor: "#1F2937",
  borderRadius: "12px",
  padding: "12px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#374151",
    transform: "translateY(-4px)",
  },
});

const DogCardImage = styled(Box)({
  width: "100%",
  paddingTop: "100%",
  position: "relative",
  borderRadius: "8px",
  marginBottom: "12px",
  backgroundColor: "#374151",
  overflow: "hidden",
  "& img": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  "& svg": {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontSize: "48px",
    color: "#6B7280",
  },
});

const DogCardMetaRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "6px",
  gap: "8px",
});

const DogCardName = styled(Typography)({
  fontSize: "15px",
  fontWeight: "600",
  color: "#FFFFFF",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  flex: 1,
  textAlign: "left",
});

const DogCardAge = styled(Typography)({
  fontSize: "15px",
  fontWeight: "600",
  color: "#FFFFFF",
  whiteSpace: "nowrap",
});

const DogCardTimeRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "6px",
  gap: "8px",
});

const DogCardTimeLabel = styled(Typography)({
  fontSize: "14px",
  fontWeight: "600",
  color: "#FFFFFF",
  whiteSpace: "nowrap",
});

const DogCardTime = styled(Typography)({
  fontSize: "14px",
  fontWeight: "600",
  color: "#FFFFFF",
  fontFamily: "monospace",
  whiteSpace: "nowrap",
});

const DogCardProgressBar = styled(LinearProgress)({
  height: "6px",
  borderRadius: "3px",
  backgroundColor: "#374151",
  "& .MuiLinearProgress-bar": {
    backgroundColor: "#10B981",
    borderRadius: "3px",
  },
});

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "400px",
  color: "#9CA3AF",
  fontSize: "18px",
});

const ShelterPage = () => {
  const { shelterSlug } = useParams();
  const { user } = useContext(UserContext);
  const { globalState, setGlobalState } = useContext(GlobalStateContext);
  const [dogs, setDogs] = useState([]);
  const [shelterInfo, setShelterInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shelterNotFound, setShelterNotFound] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Filter and sort states
  const [sortBy, setSortBy] = useState("most"); // most, least
  const [filterAge, setFilterAge] = useState("all"); // all, puppy, young, adult, senior
  const [filterPledge, setFilterPledge] = useState("all"); // all, none, partial, mostFunded

  useEffect(() => {
    const fetchShelterDogs = async () => {
      try {
        setIsLoading(true);
        setShelterNotFound(false);

        // Fetch shelter from availableShelters collection
        const sheltersSnapshot = await firestore
          .collection("availableShelters")
          .where("shelterSlug", "==", shelterSlug)
          .get();

        // Check if shelter exists
        if (sheltersSnapshot.empty) {
          setShelterNotFound(true);
          setIsLoading(false);
          return;
        }

        const shelterDoc = sheltersSnapshot.docs[0];
        const shelter = {
          id: shelterDoc.id,
          ...shelterDoc.data()
        };
        setShelterInfo(shelter);

        // Fetch config document if config_name exists
        if (shelter.config_name) {
          try {
            const configDoc = await firestore
              .collection("config")
              .doc(shelter.config_name)
              .get();

            if (configDoc.exists) {
              const configData = configDoc.data();
              console.log("Config data:", configData);
              if (configData?.lastUpdated) {
                setLastUpdated(configData.lastUpdated);
              }
            } else {
              console.log("Config document does not exist:", shelter.config_name);
            }
          } catch (error) {
            console.error("Error fetching config:", error);
          }
        }

        // Fetch all dogs from Firestore
        const dogsSnapshot = await firestore
          .collection("web-available-dogs")
          .get();

        let allDogs = dogsSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter(dog => dog.euthanasiaStatus !== 'expired' && dog.euthanasiaStatus !== 'rescued');

        // Filter dogs by shelter ID
        const filteredDogs = allDogs.filter(dog => {
          // Check if dog's shelter ID matches our shelter ID
          return dog.shelter?.id === shelter.id || dog.shelterId === shelter.id;
        });

        // Calculate time remaining for each dog
        let dogsWithTime = filteredDogs.map(dog => {
          const timeRemaining = calculateDogTimeRemaining(dog, dog.shelter?.closingTime || shelter.closingTime);
          return {
            ...dog,
            ...timeRemaining
          };
        });

        // Sort by time remaining (lowest to highest)
        dogsWithTime.sort((a, b) => {
          if (a.timeRemainingMs === -1) return 1;
          if (b.timeRemainingMs === -1) return -1;
          return a.timeRemainingMs - b.timeRemainingMs;
        });

        // Fetch all pledges to populate progress bars
        if (dogsWithTime.length > 0) {
          const pledgesSnapshot = await firestore
            .collection("pledges")
            .get();

          const allPledges = pledgesSnapshot.docs.map(doc => doc.data());
          const pledgeMap = groupPledgesByAnimalId(allPledges);

          // Enrich dogs with pledge amounts
          const enrichedDogs = dogsWithTime.map(dog => ({
            ...dog,
            pledgedAmount: pledgeMap[dog.animalId] || 0
          }));

          setDogs(enrichedDogs);
        } else {
          setDogs(dogsWithTime);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching shelter dogs:", error);
        setIsLoading(false);
      }
    };

    fetchShelterDogs();
  }, [shelterSlug]);

  // Helper function to parse age string into total months
  const parseAgeToMonths = (ageString) => {
    if (!ageString) return 0;

    const ageLower = ageString.toLowerCase();

    // Extract years
    const yearsMatch = ageLower.match(/(\d+)\s*year/);
    const years = yearsMatch ? parseInt(yearsMatch[1]) : 0;

    // Extract months
    const monthsMatch = ageLower.match(/(\d+)\s*month/);
    const months = monthsMatch ? parseInt(monthsMatch[1]) : 0;

    return years * 12 + months;
  };

  // Helper function to format phone number
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";

    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');

    // Format as (xxx)xxx-xxxx
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)})${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }

    // Return as-is if not 10 digits
    return phoneNumber;
  };

  // Apply filtering and sorting
  const filteredAndSortedDogs = useMemo(() => {
    if (!dogs || dogs.length === 0) return [];

    let filtered = [...dogs];

    // Apply age filter
    if (filterAge !== "all") {
      filtered = filtered.filter(dog => {
        const totalMonths = parseAgeToMonths(dog.age);

        switch (filterAge) {
          case "puppy": // 0-12 months
            return totalMonths <= 12;
          case "young": // 1-3 years
            return totalMonths > 12 && totalMonths <= 36;
          case "adult": // 3-7 years
            return totalMonths > 36 && totalMonths <= 84;
          case "senior": // 7+ years
            return totalMonths > 84;
          default:
            return true;
        }
      });
    }

    // Apply pledge filter
    if (filterPledge !== "all") {
      filtered = filtered.filter(dog => {
        const pledgeAmount = dog.pledgedAmount || 0;
        const pledgeGoal = 500;
        const pledgePercentage = (pledgeAmount / pledgeGoal) * 100;

        switch (filterPledge) {
          case "none": // 0% pledged
            return pledgeAmount === 0;
          case "partial": // 1-99% pledged
            return pledgePercentage > 0 && pledgePercentage < 100;
          case "mostFunded": // 80%+ pledged
            return pledgePercentage >= 80;
          default:
            return true;
        }
      });
    }

    // Apply sorting using helper function
    return sortDogsByUrgency(filtered, sortBy);
  }, [dogs, sortBy, filterAge, filterPledge]);

  return (
    <PageContainer>
      <Header />
      <MainContainer>
        {shelterNotFound ? (
          <ErrorContainer>
            <Typography variant="h5" style={{ color: "#FFFFFF", marginBottom: "8px" }}>
              Shelter Not Found
            </Typography>
            <Typography style={{ color: "#9CA3AF" }}>
              The shelter "{shelterSlug}" does not exist.
            </Typography>
          </ErrorContainer>
        ) : (
          <>
            <ShelterHeader>
              <ShelterName>
                {shelterInfo?.name || "Loading..."}
              </ShelterName>
              {shelterInfo?.location && (
                <ShelterLocation>
                  {shelterInfo.location}
                </ShelterLocation>
              )}
              {shelterInfo && (
                <ShelterContactInfo>
                  {shelterInfo.phoneNumber && (
                    <ShelterContactItem>
                      <PhoneIcon />
                      <span><a href={`tel:${shelterInfo.phoneNumber}`}>{formatPhoneNumber(shelterInfo.phoneNumber)}</a></span>
                    </ShelterContactItem>
                  )}
                  {shelterInfo.email && (
                    <ShelterContactItem>
                      <EmailIcon />
                      <span><a href={`mailto:${shelterInfo.email}`}>{shelterInfo.email}</a></span>
                    </ShelterContactItem>
                  )}
                  {shelterInfo.website && (
                    <ShelterContactItem>
                      <LanguageIcon />
                      <span><a href={shelterInfo.website} target="_blank" rel="noopener noreferrer">Visit Website</a></span>
                    </ShelterContactItem>
                  )}
                  {shelterInfo.hours && (
                    <ShelterContactItem>
                      <AccessTimeIcon />
                      <span>{shelterInfo.hours}</span>
                    </ShelterContactItem>
                  )}
                </ShelterContactInfo>
              )}
            </ShelterHeader>

            {/* Filter and Sort Controls */}
            {!isLoading && dogs.length > 0 && (
              <ControlsContainer>
                <ControlGroup>
                  <ControlLabel>Time Remaining</ControlLabel>
                  <StyledSelect
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: "#1F2937",
                          color: "#FFFFFF",
                          "& .MuiMenuItem-root": {
                            "&:hover": {
                              backgroundColor: "#374151",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#374151",
                              "&:hover": {
                                backgroundColor: "#4B5563",
                              },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="most">Most Urgent</MenuItem>
                    <MenuItem value="least">Least Urgent</MenuItem>
                  </StyledSelect>
                </ControlGroup>

                <ControlGroup>
                  <ControlLabel>Age</ControlLabel>
                  <StyledSelect
                    value={filterAge}
                    onChange={(e) => setFilterAge(e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: "#1F2937",
                          color: "#FFFFFF",
                          "& .MuiMenuItem-root": {
                            "&:hover": {
                              backgroundColor: "#374151",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#374151",
                              "&:hover": {
                                backgroundColor: "#4B5563",
                              },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">All Ages</MenuItem>
                    <MenuItem value="puppy">Puppy (0-1 yr)</MenuItem>
                    <MenuItem value="young">Young (1-3 yrs)</MenuItem>
                    <MenuItem value="adult">Adult (3-7 yrs)</MenuItem>
                    <MenuItem value="senior">Senior (7+ yrs)</MenuItem>
                  </StyledSelect>
                </ControlGroup>

                <ControlGroup>
                  <ControlLabel>Pledge Status</ControlLabel>
                  <StyledSelect
                    value={filterPledge}
                    onChange={(e) => setFilterPledge(e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: "#1F2937",
                          color: "#FFFFFF",
                          "& .MuiMenuItem-root": {
                            "&:hover": {
                              backgroundColor: "#374151",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#374151",
                              "&:hover": {
                                backgroundColor: "#4B5563",
                              },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">All Pledges</MenuItem>
                    <MenuItem value="none">No Pledges</MenuItem>
                    <MenuItem value="partial">Partially Funded</MenuItem>
                    <MenuItem value="mostFunded">80%+ Funded</MenuItem>
                  </StyledSelect>
                </ControlGroup>
              </ControlsContainer>
            )}

            {/* Results Count */}
            {!isLoading && dogs.length > 0 && (
              <ResultsCount>
                <span>Showing {filteredAndSortedDogs.length} of {dogs.length} dog{dogs.length !== 1 ? 's' : ''}</span>
                {lastUpdated && (
                  <span>Last Updated: {lastUpdated}</span>
                )}
              </ResultsCount>
            )}

            {isLoading ? (
              <LoadingContainer>Loading dogs...</LoadingContainer>
            ) : dogs.length === 0 ? (
              <ErrorContainer>
                <Typography variant="h6" style={{ color: "#FFFFFF", marginBottom: "8px" }}>
                  No Dogs Found
                </Typography>
                <Typography style={{ color: "#9CA3AF" }}>
                  There are currently no dogs on the euthanasia list for this shelter.
                </Typography>
              </ErrorContainer>
            ) : filteredAndSortedDogs.length === 0 ? (
              <ErrorContainer>
                <Typography variant="h6" style={{ color: "#FFFFFF", marginBottom: "8px" }}>
                  No Dogs Match Filters
                </Typography>
                <Typography style={{ color: "#9CA3AF" }}>
                  Try adjusting your filters to see more dogs.
                </Typography>
              </ErrorContainer>
            ) : (
              <DogGrid>
                {filteredAndSortedDogs.map((dog) => {
                  const primaryMedia = dog.videos?.[0] || dog.images?.[0];
                  const pledgeGoal = 500;
                  const pledgePercentage = Math.min(((dog.pledgedAmount || 0) / pledgeGoal) * 100, 100);

                  return (
                    <Link
                      to={`/${dog.slug}`}
                      key={dog.id}
                      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                    >
                      <DogCard>
                        <DogCardImage>
                          {primaryMedia ? (
                            <img src={primaryMedia} alt={dog.name} />
                          ) : (
                            <PetsIcon />
                          )}
                        </DogCardImage>
                        <DogCardMetaRow>
                          <DogCardName>{dog.name}</DogCardName>
                          <DogCardAge>{formatDogAge(dog.age)}</DogCardAge>
                        </DogCardMetaRow>
                        <DogCardTimeRow>
                          <DogCardTimeLabel>⏰</DogCardTimeLabel>
                          <DogCardTime>
                            {dog.timeRemainingFormatted} LEFT
                          </DogCardTime>
                        </DogCardTimeRow>
                        <DogCardTimeRow>
                          <DogCardTimeLabel>Days at Shelter</DogCardTimeLabel>
                          <DogCardTime>
                            {dog.daysAtShelter || 'N/A'}
                          </DogCardTime>
                        </DogCardTimeRow>
                        <DogCardProgressBar
                          variant="determinate"
                          value={Math.max(3, pledgePercentage)}
                        />
                      </DogCard>
                    </Link>
                  );
                })}
              </DogGrid>
            )}
          </>
        )}
      </MainContainer>

      <FooterComponent />

      {/* Auth Modal */}
      {globalState.showAuthModal && <AuthModal />}
    </PageContainer>
  );
};

export default ShelterPage;
